import React, { useState, useCallback } from 'react';
import { Alert } from '@mui/material';

import { ProjectDetailPageProps } from '../../../types';
import { useFinancingData } from './hooks/useFinancingData';
import { usePageWarnings } from '../../../contexts/PageWarningsContext';
import PageTemplate from '../../layout/PageTemplate';
import InvestmentSection from './sections/InvestmentSection';
import LoanSection from './sections/LoanSection';
import RepaymentSection from './sections/RepaymentSection';

interface FinancingPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
}

/**
 * Financing Page Component
 * Zeigt die Finanzierungsstruktur mit Darlehen und aggregiertem Zins-/Tilgungsplan.
 *
 * Architektur:
 * - useFinancingData: Lädt Financing + CAPEX (für InvestmentSection + LoanSection)
 * - RepaymentSection: Lädt den Tilgungsplan eigenständig über useRepaymentPlan
 * - repaymentVersion (Counter): Signalisiert der RepaymentSection, dass sie nach
 *   einer Darlehens-Mutation neu laden soll – ohne die anderen Sections zu blockieren
 */
const FinancingPage: React.FC<FinancingPageProps> = ({
  project,
  canEdit,
}) => {
  const { setValidationsFromAPI, getValidationEntries } = usePageWarnings();

  const {
    financing,
    loans,
    darlehensTypOptions,
    gesamtinvestition,
    eigenkapital,
    fremdkapital,
    summeDarlehen,
    isLoading,
    error,
    isSaving,
    updateFinancingField,
    reloadLoans,
  } = useFinancingData({
    projectId: project.id,
    onValidationsReceived: setValidationsFromAPI,
  });

  // Die Finanzierungslücke berechnet das Backend (siehe ADR 001) – hier wird sie nur angezeigt
  const finanzierungsluecke = getValidationEntries('project-financing').find(
    entry => entry.code === 'FINANCING_GAP',
  );

  // Version-Counter: Jede Inkrementierung triggert einen Tilgungsplan-Reload in der RepaymentSection
  const [repaymentVersion, setRepaymentVersion] = useState(0);

  /**
   * Handler für Darlehens-Mutationen (Create/Update/Delete).
   * Aktualisiert die Loan-Daten UND signalisiert der RepaymentSection einen Reload.
   * Die RepaymentSection lädt den Tilgungsplan eigenständig nach,
   * während InvestmentSection und LoanSection sofort aktuell sind.
   */
  const handleDataChanged = useCallback(async () => {
    await reloadLoans();
    setRepaymentVersion(v => v + 1);
  }, [reloadLoans]);

  /**
   * Handler für Eigenkapitalquote-Änderung (direkt vom Slider).
   * Das Backend passt automatisch das Hauptdarlehen an und berechnet den Tilgungsplan neu.
   * Wir müssen daher nach dem Update auch den Tilgungsplan in der RepaymentSection neu laden.
   */
  const handleEigenkapitalquoteChange = useCallback(async (value: number) => {
    await updateFinancingField('eigenkapitalquote', value);
    setRepaymentVersion(v => v + 1);
  }, [updateFinancingField]);

  return (
    <PageTemplate
      isLoading={isLoading}
      error={error && !financing ? error : undefined}
      isSaving={isSaving}
      saveError={error}
    >
      {finanzierungsluecke && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>Finanzierungslücke:</strong> {finanzierungsluecke.message}
        </Alert>
      )}

      {/* Investitionssektion */}
      <InvestmentSection
        gesamtinvestition={gesamtinvestition}
        eigenkapitalquote={financing?.eigenkapitalquote}
        eigenkapital={eigenkapital}
        fremdkapital={fremdkapital}
        summeDarlehen={summeDarlehen}
        canEdit={canEdit}
        onEigenkapitalquoteChange={handleEigenkapitalquoteChange}
      />

      {/* Darlehenssektion (Tabelle mit allen Darlehen) */}
      <LoanSection
        loans={loans}
        darlehensTypOptions={darlehensTypOptions}
        canEdit={canEdit}
        projektLaufzeit={project.laufzeit_jahre!}
        fremdkapital={fremdkapital}
        summeDarlehen={summeDarlehen}
        onLoanDeleted={handleDataChanged}
        onDataChanged={handleDataChanged}
        onValidationsReceived={setValidationsFromAPI}
      />

      {/* Aggregierter Zins- und Tilgungsplan – lädt eigenständig über useRepaymentPlan */}
      <RepaymentSection
        projectId={project.id}
        reloadTrigger={repaymentVersion}
      />
    </PageTemplate>
  );
};

export default FinancingPage;
